import { useMemo, useState, type FunctionComponent } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Popconfirm, Space, Table, Tag, Typography, message } from "antd";

import {
  asBadgeCategory,
  asBadgeRuleType,
  type BadgeDefinitionEntity,
} from "@fitness-app/data-models/entities/Badge";

import { supabase } from "~/store/initializeStore";
import BadgeFormDrawer from "./BadgeFormDrawer";
import { badgeCategoryColors, badgeCategoryLabels, badgeRuleTypeLabels } from "./constants";
import { formatTiers } from "./helpers";

const BadgesListPage: FunctionComponent = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState<BadgeDefinitionEntity | null>(null);

  const badgesQuery = useQuery({
    queryKey: ["badges-list"],
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("badge_definition")
        .select("*")
        .eq("archived", false)
        .order("sortOrder", { ascending: true });

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  // Per-definition counts come pre-aggregated from the badge_earned_count view — counting raw
  // client_badge rows here silently hit PostgREST's 1000-row cap and under-reported every badge
  // (TRE-1756). The view is security_invoker, so the counts stay scoped by the same RLS.
  const earnedCountsQuery = useQuery({
    queryKey: ["badges-earned-counts"],
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("badge_earned_count")
        .select("badgeKey, earnedCount")
        .returns<{ badgeKey: string; earnedCount: number }[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const earnedCountByKey = useMemo(
    () => new Map((earnedCountsQuery.data ?? []).map((row) => [row.badgeKey, row.earnedCount])),
    [earnedCountsQuery.data],
  );

  const archiveBadgeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("badge_definition")
        .update({ archived: true, updatedAt: new Date().toISOString() } as never)
        .eq("id", id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Odznaka zarchiwizowana");
      void queryClient.invalidateQueries({ queryKey: ["badges-list"] });
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zarchiwizować odznaki: ${error.message}`);
    },
  });

  const openCreate = () => {
    setEditingBadge(null);
    setIsFormOpen(true);
  };

  const openEdit = (badge: BadgeDefinitionEntity) => {
    setEditingBadge(badge);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingBadge(null);
  };

  return (
    <Card
      title="Odznaki"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nowa odznaka
        </Button>
      }
    >
      <Table<BadgeDefinitionEntity>
        dataSource={badgesQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={badgesQuery.isFetching}
        locale={{ emptyText: "Brak odznak" }}
        pagination={false}
      >
        <Table.Column<BadgeDefinitionEntity>
          title="Ikona"
          dataIndex="icon"
          key="icon"
          width={70}
          render={(icon: string) => <span style={{ fontSize: 28 }}>{icon}</span>}
        />
        <Table.Column<BadgeDefinitionEntity>
          title="Nazwa"
          dataIndex="name"
          key="name"
          render={(name: string, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{name}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.key}
              </Typography.Text>
              {row.description ? (
                <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320 }}>
                  {row.description}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<BadgeDefinitionEntity>
          title="Kategoria"
          dataIndex="category"
          key="category"
          render={(category: string) => {
            const badgeCategory = asBadgeCategory(category);

            return <Tag color={badgeCategoryColors[badgeCategory] ?? "default"}>{badgeCategoryLabels[badgeCategory]}</Tag>;
          }}
        />
        <Table.Column<BadgeDefinitionEntity>
          title="Reguła"
          dataIndex="ruleType"
          key="ruleType"
          render={(ruleType: string, row) => {
            const tiersText = formatTiers(row.config);

            return (
              <Space direction="vertical" size={0}>
                <Typography.Text>{badgeRuleTypeLabels[asBadgeRuleType(ruleType)]}</Typography.Text>
                {tiersText ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {tiersText}
                  </Typography.Text>
                ) : null}
              </Space>
            );
          }}
        />
        <Table.Column<BadgeDefinitionEntity>
          title="Zdobyte"
          key="earned"
          width={100}
          render={(_, row) => earnedCountByKey.get(row.key) ?? 0}
        />
        <Table.Column<BadgeDefinitionEntity>
          title="Opcje"
          key="action"
          render={(_, row) => (
            <Space size="middle">
              <Button type="link" className="px-0" onClick={() => openEdit(row)}>
                Edytuj
              </Button>
              <Popconfirm
                title="Zarchiwizować odznakę?"
                description="Odznaka zniknie z listy, ale zdobyte odznaki klientek zostaną zachowane."
                okText="Archiwizuj"
                cancelText="Anuluj"
                okButtonProps={{ danger: true, loading: archiveBadgeMutation.isLoading }}
                onConfirm={() => archiveBadgeMutation.mutate(row.id)}
              >
                <Button type="link" danger className="px-0">
                  Archiwizuj
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <BadgeFormDrawer open={isFormOpen} badge={editingBadge} onClose={closeForm} onSaved={closeForm} />
    </Card>
  );
};

export default BadgesListPage;
